import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import MobilePromotionForm from '../molecules/MobilePromotionForm'
import PromotionPhotosField from '../molecules/PromotionPhotosField'
import ChevronRightSvg from '../other/svg/ChevronRightSvg'
import {
  MobilePromotionFormData,
  mobilePromotionFormFromPromotion,
  mobilePromotionHighlights
} from 'src/helpers/mobilePromotionFormValidation'
import { FETCH_BRANDS } from 'src/redux/brandsReducer'
import { FETCH_CHANNELS } from 'src/redux/integrationsReducer'
import { FETCH_MOBILE_REFERENCE_VALUES } from 'src/redux/mobileReferenceReducer'
import { FETCH_MOTORBIKE_PHOTOS } from 'src/redux/photosReducer'
import { FETCH_PROMOTIONS, RESET_PROMOTION, UPDATE_PROMOTION } from 'src/redux/promotionsReducer'
import { RootReducer } from 'src/redux/rootReducer'

const PromotionEdit = (): JSX.Element => {
  const { t } = useTranslation()
  const { id, promotionId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const brands = useSelector((state: RootReducer) => state.brandsReducer.brands)
  const referenceColors = useSelector((state: RootReducer) => state.mobileReferenceReducer.colors)
  const referenceCategories = useSelector((state: RootReducer) => state.mobileReferenceReducer.categories)
  const referenceGearBoxes = useSelector((state: RootReducer) => state.mobileReferenceReducer.gearBoxes)
  const referenceDriveTypes = useSelector((state: RootReducer) => state.mobileReferenceReducer.driveTypes)
  const referenceVatRates = useSelector((state: RootReducer) => state.mobileReferenceReducer.vatRates)
  const loading = useSelector((state: RootReducer) => state.promotionsReducer.loading)
  const error = useSelector((state: RootReducer) => state.promotionsReducer.error)
  const apiError = useSelector((state: RootReducer) => state.promotionsReducer.errorMessage)
  const updated = useSelector((state: RootReducer) => state.promotionsReducer.updated)
  const loaded = useSelector((state: RootReducer) => state.promotionsReducer.loaded)
  const promotion = useSelector(
    (state: RootReducer) => state.promotionsReducer.promotions.find((item) => String(item.id) === promotionId) ?? null
  )
  const vehiclePath = typeof id === 'string' ? `/selling/vehicles/${id}` : '/selling/vehicles'
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[] | null>(null)
  const [selectedCoverId, setSelectedCoverId] = useState<string | null>(null)
  const photosState = useSelector((state: RootReducer) => state.photosReducer)
  const photos = photosState.motorbikeId === id ? photosState.photos : []
  const maxImages = useSelector((state: RootReducer) => {
    const channel = state.integrationsReducer.channels.find((item) => item.uniq_name === 'mobile.de')
    const limit = channel?.activation?.max_images
    return typeof limit === 'number' ? limit : null
  })
  const photoIds = selectedPhotoIds ?? promotion?.photo_ids ?? []
  const coverId = selectedPhotoIds === null ? promotion?.cover_photo_id ?? null : selectedCoverId

  useEffect(() => {
    dispatch(FETCH_BRANDS())
    dispatch(FETCH_CHANNELS())
    dispatch(FETCH_MOBILE_REFERENCE_VALUES())
    if (typeof id === 'string') {
      dispatch(FETCH_PROMOTIONS(id))
      dispatch(FETCH_MOTORBIKE_PHOTOS(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (typeof id !== 'string' || typeof promotionId !== 'string') {
      navigate(vehiclePath)
      return
    }
    if (loaded && promotion === null) {
      navigate(vehiclePath)
    }
  }, [id, loaded, navigate, promotion, promotionId, vehiclePath])

  useEffect(() => {
    return () => {
      dispatch(RESET_PROMOTION())
    }
  }, [dispatch])

  useEffect(() => {
    if (!updated) {
      return
    }
    dispatch(RESET_PROMOTION())
    navigate(vehiclePath)
  }, [updated, dispatch, navigate, vehiclePath])

  const handleCancel = (): void => {
    navigate(vehiclePath)
  }

  const handleSubmit = (values: MobilePromotionFormData): void => {
    if (typeof id !== 'string' || promotion === null || promotion.portal !== 'mobile.de') {
      return
    }
    dispatch(
      UPDATE_PROMOTION({
        ...values,
        motorbikeId: id,
        promotionId: promotion.id,
        portal: promotion.portal,
        highlights: mobilePromotionHighlights(values),
        photoIds: selectedPhotoIds,
        coverPhotoId: selectedPhotoIds === null ? null : selectedCoverId
      })
    )
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('my_vehicles.promotion')}
            </button>
            <ChevronRightSvg className="size-3.5 text-foregrounds-fg-on-color" />
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('my_vehicles.edit_promotion')}
            </div>
          </div>
        </div>
        {promotion !== null && (
          <PromotionPhotosField
            photos={photos}
            selectedIds={photoIds}
            coverId={coverId}
            maxImages={maxImages}
            onChange={(ids, nextCoverId) => {
              setSelectedPhotoIds(ids)
              setSelectedCoverId(nextCoverId)
            }}
          />
        )}
        {promotion !== null && (
          <MobilePromotionForm
            initialValues={mobilePromotionFormFromPromotion(promotion)}
            brands={brands}
            referenceColors={referenceColors}
            referenceCategories={referenceCategories}
            referenceGearBoxes={referenceGearBoxes}
            referenceDriveTypes={referenceDriveTypes}
            referenceVatRates={referenceVatRates}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            error={error}
            apiError={apiError}
            submitLabel={t('my_vehicles.save')}
            errorMessage={t('my_vehicles.promotion_update_error')}
          />
        )}
      </div>
    </Layout>
  )
}

export default PromotionEdit
